import { useCallback, useEffect, useRef, useState, type DependencyList, type ReactNode } from "react";
import type { Book, EntityType, Identification, JobStatus } from "./api";

export function useLoad<T>(fn: () => Promise<T>, deps: DependencyList) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const reload = useCallback(() => {
    const id = ++seq.current;
    setLoading(true);
    fn()
      .then((d) => {
        if (id === seq.current) {
          setData(d);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (id === seq.current) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (id === seq.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(reload, [reload]);
  return { data, error, loading, reload, setData };
}

export function ErrorNote({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="note note-error" role="alert">
      {error}
    </p>
  );
}

export function Spinner({ label = "Laden…" }: { label?: string }) {
  return (
    <p className="muted" aria-live="polite">
      {label}
    </p>
  );
}

export const ENTITY_TYPES: { type: EntityType; label: string }[] = [
  { type: "character", label: "Personages" },
  { type: "place", label: "Plaatsen" },
  { type: "object", label: "Voorwerpen" },
  { type: "group", label: "Groepen" },
];

export const FACT_TYPES: Record<string, string> = {
  status: "Status",
  background: "Achtergrond",
  trait: "Eigenschap",
  action: "Handeling",
  death: "Dood",
  other: "Overig",
};

// [label when the entity is the subject, label when it is the object]
const RELATIONS: Record<string, [string, string]> = {
  parent_of: ["ouder van", "kind van"],
  child_of: ["kind van", "ouder van"],
  sibling_of: ["broer/zus van", "broer/zus van"],
  relative_of: ["familie van", "familie van"],
  married_to: ["getrouwd met", "getrouwd met"],
  heir_of: ["erfgenaam van", "heeft als erfgenaam"],
  loves: ["houdt van", "geliefd door"],
  friend_of: ["bevriend met", "bevriend met"],
  ally_of: ["bondgenoot van", "bondgenoot van"],
  enemy_of: ["vijand van", "vijand van"],
  serves: ["dient", "gediend door"],
  member_of: ["lid van", "heeft als lid"],
  leader_of: ["leider van", "geleid door"],
  owns: ["bezit", "in bezit van"],
  lives_in: ["woont in", "woonplaats van"],
  located_in: ["ligt in", "omvat"],
  travels_with: ["reist met", "reist met"],
  kills: ["doodt", "gedood door"],
};

export function relationLabel(type: string, direction: "outgoing" | "incoming"): string {
  const pair = RELATIONS[type];
  if (!pair) return type.replace(/_/g, " ");
  return direction === "outgoing" ? pair[0] : pair[1];
}

const JOB_LABELS: Record<JobStatus, string> = {
  queued: "In de wachtrij",
  running: "Wordt verwerkt",
  succeeded: "Klaar",
  failed: "Mislukt",
  cancelled: "Geannuleerd",
  budget_exceeded: "Deels verwerkt",
};

export function JobBadge({
  status,
  stage,
  notBefore,
}: {
  status: JobStatus | null;
  stage?: string | null;
  notBefore?: string | null;
}) {
  if (!status) return null;
  if (status === "queued" && notBefore && new Date(notBefore) > new Date()) {
    const at = new Date(notBefore).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
    return (
      <span className="badge badge-queued" title="Verwerking in de goedkope daluren">
        Gepland na {at}
      </span>
    );
  }
  const busy = status === "queued" || status === "running";
  return (
    <span className={`badge badge-${status}`}>
      {JOB_LABELS[status]}
      {busy && stage && stage !== status ? ` · ${stage}` : ""}
    </span>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function usd(value: number | string): string {
  const n = typeof value === "string" ? Number(value) : value;
  return `$${n.toFixed(n < 1 ? 4 : 2)}`;
}

type LegalFields = Pick<Book, "visibility" | "rights_basis" | "provenance" | "public_domain"> &
  Partial<Pick<Book, "rights_basis_by">>;

/** Who stated the sharing basis: the owner when publishing, or an administrator. */
function basisBy(book: Partial<Pick<Book, "rights_basis_by">>): string {
  return book.rights_basis_by === "owner" ? "opgegeven door de eigenaar" : "vastgesteld door beheerder";
}
type Tone = "ok" | "warn" | "danger" | "muted";

/** The legal status of a book as short labels: sharing, provenance check, public domain. */
export function legalStatus(book: LegalFields): { label: string; tone: Tone; title: string }[] {
  const out: { label: string; tone: Tone; title: string }[] = [];
  const basis = book.rights_basis ?? "private";
  if (book.visibility === "public") {
    out.push({
      label: basis === "private" ? "Openbaar zonder grondslag" : "Openbaar",
      tone: basis === "private" ? "danger" : "ok",
      title: "Iedereen met een account kan deze wiki lezen.",
    });
  } else {
    out.push({ label: "Privé", tone: "muted", title: "Alleen de eigenaar ziet de inhoud." });
  }
  if (basis === "public_domain")
    out.push({ label: "Grondslag: publiek domein", tone: "ok", title: `Grondslag ${basisBy(book)}.` });
  if (basis === "licensed")
    out.push({ label: "Grondslag: licentie", tone: "ok", title: `Grondslag ${basisBy(book)}.` });

  const p = book.provenance ?? {};
  const findings = p.findings ?? [];
  const summary = findings.map((f) => `• ${f.message}`).join("\n");
  if (!p.sha256) {
    out.push({
      label: "Herkomst niet gecontroleerd",
      tone: "muted",
      title: "Geüpload vóór de herkomstcontrole bestond (oktober 2026).",
    });
  } else if (p.risk === "high") {
    out.push({
      label: p.acknowledged ? "Herkomst: waarschuwing bevestigd" : "Herkomst: waarschuwing",
      tone: "danger",
      title: summary,
    });
  } else {
    out.push({
      label: findings.some((f) => f.code === "public_domain_source") ? "Herkomst: vrije bron" : "Herkomst in orde",
      tone: "ok",
      title: summary || "Geen DRM, voorbehoud of illegale bron gevonden.",
    });
  }

  const pd = book.public_domain ?? {};
  const reasons = (pd.reasons ?? []).join("\n");
  if (pd.status === "likely_public_domain")
    out.push({ label: "Waarschijnlijk publiek domein", tone: "ok", title: `${reasons}\n${pd.note ?? ""}` });
  else if (pd.status === "protected")
    out.push({
      label: pd.free_from_year ? `Auteursrecht tot ${pd.free_from_year - 1}` : "Auteursrecht loopt",
      tone: "muted",
      title: reasons,
    });
  else if (pd.status === "unknown")
    out.push({ label: "Publiek domein: onbekend", tone: "muted", title: reasons });
  return out;
}

export function LegalBadges({ book }: { book: LegalFields }) {
  return (
    <span className="legal-badges" aria-label="Rechtenstatus">
      {legalStatus(book).map((s) => (
        <span key={s.label} className={`badge badge-${s.tone}`} title={s.title}>
          {s.label}
        </span>
      ))}
    </span>
  );
}

export const PD_LABELS: Record<string, string> = {
  likely_public_domain: "Waarschijnlijk publiek domein",
  protected: "Beschermd (auteursrecht loopt nog)",
  unknown: "Onbekend",
};

const IDENTIFY_METHODS: Record<string, string> = {
  epub_isbn_lookup: "ISBN uit het e-book, opgezocht in boekcatalogi",
  epub_isbn_fallback: "ISBN uit het e-book (niet gevonden in boekcatalogi)",
  epub_passage_search: "tekstfragment uit het begin, gezocht in boekcatalogi",
  epub_metadata_only: "alleen de gegevens in het e-bookbestand",
};

const SOURCES: Record<string, string> = {
  open_library: "Open Library",
  google_books: "Google Books",
  epub_metadata: "e-bookbestand",
};

/** "nl" -> "Nederlands"; unknown codes are shown as they are. */
export function languageName(code: string | null | undefined): string | null {
  if (!code) return null;
  try {
    const name = new Intl.DisplayNames(["nl"], { type: "language" }).of(code);
    return name && name !== code ? `${name.charAt(0).toUpperCase()}${name.slice(1)} (${code})` : code;
  } catch {
    return code;
  }
}

function sourceName(source: string): string {
  return source
    .split("+")
    .map((s) => SOURCES[s] ?? s)
    .join(" + ");
}

/** May the book be used freely? A stated basis wins over the advisory check. */
function freeToUse(book: Pick<Book, "rights_basis" | "rights_basis_by" | "public_domain">): ReactNode {
  if (book.rights_basis === "public_domain") return `Ja: publiek domein (${basisBy(book)})`;
  if (book.rights_basis === "licensed") return `Ja: met licentie (${basisBy(book)})`;
  const pd = book.public_domain ?? {};
  if (!pd.status) return <span className="muted">nog niet gecontroleerd</span>;
  return (
    <>
      {pd.status === "likely_public_domain"
        ? "Waarschijnlijk wel (publiek domein)"
        : pd.status === "protected"
          ? `Nee, auteursrecht loopt nog${pd.free_from_year ? ` (vrij vanaf ${pd.free_from_year})` : ""}`
          : "Onbekend"}
      {pd.reasons?.length ? <span className="muted small"> ({pd.reasons.join("; ")})</span> : null}
      {pd.note && (
        <>
          <br />
          <span className="muted small">{pd.note}</span>
        </>
      )}
    </>
  );
}

/** "Austen, Jane" and "Jane Austen" are the same author. */
function sameAuthors(a: string[] | null | undefined, b: string[] | null | undefined): boolean {
  const name = (x: string) => {
    const [last, first] = x.split(",", 2);
    return (first === undefined ? x : `${first} ${last}`).trim().toLowerCase().replace(/\s+/g, " ");
  };
  const norm = (l: string[] | null | undefined) => (l ?? []).map(name).sort().join("|");
  return norm(a) === norm(b);
}

/** Bibliographic data: what identification found and what the e-book file itself says. */
export function BookInfo({ book }: { book: Book }) {
  const ident: Identification | null = book.identification ?? null;
  const sel = ident?.selected ?? null;
  const epub = ident?.epub;
  const isbn10 = sel?.isbn_10 && sel.isbn_10 !== book.isbn ? sel.isbn_10 : null;
  const otherIsbns = (epub?.isbns ?? []).filter((i) => i !== book.isbn && i !== sel?.isbn_10);
  const fileDiffers =
    epub && (epub.title || epub.authors?.length)
      ? (epub.title ?? "").trim().toLowerCase() !== book.title.trim().toLowerCase() ||
        !sameAuthors(epub.authors, book.authors)
      : false;
  return (
    <dl className="facts-grid">
      <dt>Titel</dt>
      <dd>{book.title}</dd>
      <dt>{book.authors.length > 1 ? "Schrijvers" : "Schrijver"}</dt>
      <dd>{book.authors.join(", ") || <span className="muted">onbekend</span>}</dd>
      <dt>ISBN</dt>
      <dd>
        {book.isbn ?? <span className="muted">niet gevonden</span>}
        {isbn10 && <span className="muted"> (ISBN-10 {isbn10})</span>}
        {otherIsbns.length > 0 && (
          <span className="muted small"> — in het bestand ook: {otherIsbns.join(", ")}</span>
        )}
      </dd>
      <dt>Taal</dt>
      <dd>{languageName(book.language ?? ident?.language) ?? <span className="muted">onbekend</span>}</dd>
      {(sel?.publisher || epub?.publisher) && (
        <>
          <dt>Uitgever</dt>
          <dd>{sel?.publisher || epub?.publisher}</dd>
        </>
      )}
      {(sel?.publish_year || epub?.date) && (
        <>
          <dt>Uitgegeven</dt>
          <dd>{sel?.publish_year ?? epub?.date?.slice(0, 10)}</dd>
        </>
      )}
      <dt>Vrij te gebruiken</dt>
      <dd>{freeToUse(book)}</dd>
      {epub?.rights && (
        <>
          <dt>Rechten volgens het bestand</dt>
          <dd className="small">{epub.rights}</dd>
        </>
      )}
      {fileDiffers && (
        <>
          <dt>Volgens het bestand</dt>
          <dd className="small">
            {epub?.title ?? "zonder titel"}
            {epub?.authors?.length ? ` — ${epub.authors.join(", ")}` : ""}
          </dd>
        </>
      )}
      <dt>Gevonden via</dt>
      <dd className="small">
        {!ident ? (
          <span className="muted">geen identificatiegegevens bewaard (boek van vóór deze functie, of opzoeken mislukt)</span>
        ) : (
          <>
            {IDENTIFY_METHODS[ident.method ?? ""] ?? ident.method ?? "onbekend"}
            {sel && sel.source && ` — bron: ${sourceName(sel.source)}`}
            {sel && ident.method === "epub_passage_search" && (
              <span className="muted"> (zekerheid {Math.round(sel.confidence_score * 100)}%)</span>
            )}
            <span className="muted"> · {ident.identified_on}</span>
          </>
        )}
      </dd>
    </dl>
  );
}
